import { LinearProgress } from "@octopusdeploy/design-system-components";
import { AccountType, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import * as React from "react";
import { useAnalyticTrackedActionDispatch, useCorrelationId } from "~/analytics/Analytics";
import { AccountEditInternal, type AccountInitialData } from "~/areas/infrastructure/components/AccountEdit/AddOrEditAccountPage";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { repository } from "~/clientInstance";
import DataLoader from "~/components/DataLoader";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { AccountDrawerControlProps } from "./AccountEditBase";
interface AccountDrawerProps extends AccountDrawerControlProps {
    newAccountType: AccountType;
}
const AccountDrawerDataLoader = DataLoader<AccountInitialData>();
export function AccountDrawer({ newAccountType, isOpen, setIsOpen, onSuccess: onSave }: AccountDrawerProps) {
    const trackAction = useAnalyticTrackedActionDispatch();
    const trackEvent = useTrackEvent();
    const correlationId = useCorrelationId();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const loadData = async (): Promise<AccountInitialData> => {
        const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository);
        const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
        const azureEnvironments = repository.Accounts.getIsolatedAzureEnvironments();
        return {
            environments: environments,
            tenants: await tenants,
            azureEnvironments: await azureEnvironments,
            accountType: newAccountType,
        };
    };
    if (!isOpen) {
        return null;
    }
    return (<PermissionCheck permission={Permission.AccountCreate} environment="*" tenant="*" key="perms">
            <AccountDrawerDataLoader load={loadData} renderWhenLoaded={function (data: AccountInitialData): React.ReactNode {
            return (newAccountType !== AccountType.None && (<AccountEditInternal initialData={{ ...data, accountType: newAccountType }} trackAction={trackAction} trackEvent={trackEvent} correlationId={correlationId} type="drawer" isOpen={isOpen} setIsOpen={setIsOpen} onSuccess={onSave}></AccountEditInternal>));
        }} renderAlternate={({ busy, errors }) => <LinearProgress show={busy} variant="indeterminate"></LinearProgress>}></AccountDrawerDataLoader>
        </PermissionCheck>);
}
